import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	contentChildren,
	CUSTOM_ELEMENTS_SCHEMA,
	input,
	model,
	signal,
} from '@angular/core';

import { TabMenuItemDirective } from './tab-menu.directive';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-tab-menu`,
	templateUrl: './tab-menu.html',
	imports: [CommonModule],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TabMenuComponent {
	activeTab = model(0);

	color = input<'white' | 'accent'>('white');

	useButtonVariant = input(false);
	// Is only being used if useButtonVariant is true
	numberOfColumns = input<2 | 3 | 4>(3);
	// Is only being used if useButtonVariant is true
	hideTabs = input(false);

	tabMenuItems = contentChildren(TabMenuItemDirective);

	// Is only being used if useButtonVariant is true
	numberOfColumnsTab = computed<string>(() => {
		switch (this.numberOfColumns()) {
			case 2:
				return 'md:grid-cols-2';
			case 3:
				return 'md:grid-cols-3';
			case 4:
				return 'md:grid-cols-4';
		}
	});

	tabNames = computed<string[]>(
		() => this.tabMenuItems()?.map((item) => item.name) ?? [],
	);

	backgroundColor = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'bg-white';
			case 'accent':
				return 'bg-accent-25';
			default:
				return '';
		}
	});

	startGradient = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'from-white';
			case 'accent':
				return 'from-accent-25';
			default:
				return '';
		}
	});

	endGradient = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'to-white';
			case 'accent':
				return 'to-accent-25';
			default:
				return '';
		}
	});

	setActiveTab(tabIndex: number) {
		this.activeTab.set(tabIndex);
	}

	private readonly scrollLeft = signal(0);
	private readonly scrollWidth = signal(0);
	private readonly clientWidth = signal(0);
	private readonly isInitialized = signal(false);

	isAtStart = computed(() => {
		if (!this.isInitialized()) {
			return true;
		}
		return this.scrollLeft() <= 0;
	});

	isAtEnd = computed(() => {
		if (!this.isInitialized()) {
			return false;
		}
		return this.scrollLeft() >= this.scrollWidth() - this.clientWidth() - 1;
	});

	canScroll = computed(() => {
		if (!this.isInitialized()) {
			return true;
		}
		return this.scrollWidth() > this.clientWidth();
	});

	onWheel(event: WheelEvent) {
		const element = event.currentTarget as HTMLElement;
		if (
			element &&
			this.canScroll() &&
			Math.abs(event.deltaY) > Math.abs(event.deltaX)
		) {
			event.preventDefault();
			element.scrollLeft += event.deltaY;
			this.updateScrollPosition(element);
		}
	}

	onScroll(event: Event) {
		const element = event.currentTarget as HTMLElement;
		this.updateScrollPosition(element);
	}

	onTabKeydown(event: KeyboardEvent, currentIndex: number) {
		const tabCount = this.tabMenuItems().length;

		switch (event.key) {
			case 'ArrowLeft': {
				event.preventDefault();
				const prevIndex =
					currentIndex > 0 ? currentIndex - 1 : tabCount - 1;
				this.setActiveTab(prevIndex);
				this.focusTab(prevIndex);
				break;
			}
			case 'ArrowRight': {
				event.preventDefault();
				const nextIndex =
					currentIndex < tabCount - 1 ? currentIndex + 1 : 0;
				this.setActiveTab(nextIndex);
				this.focusTab(nextIndex);
				break;
			}
			default:
				break;
		}
	}

	private focusTab(index: number) {
		setTimeout(() => {
			const tabElement = document.querySelector(
				`[data-name="${this.tabMenuItems()[index]?.name}"]`,
			);
			if (tabElement) {
				(tabElement as HTMLElement).focus();
				tabElement.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center',
				});
			}
		}, 0);
	}

	private updateScrollPosition(element: HTMLElement) {
		this.scrollLeft.set(element.scrollLeft);
		this.scrollWidth.set(element.scrollWidth);
		this.clientWidth.set(element.clientWidth);
		this.isInitialized.set(true);
	}
}
